/*
 * Rift Shop → custom checkout handoff (theriftshop.us and riftshop.us).
 *
 * Every way a shopper reaches Shopify's checkout — the VIP auto-add redirect after Add to cart,
 * the cart drawer's Checkout button, and the cart page's Checkout button — goes through
 * `goToCheckout()` here instead.
 *
 * The shopper is sent to the checkout page immediately with the cart lines in the URL fragment
 * (`#c=…`, never sent to any server as part of the URL). The checkout page opens at once and asks
 * its own server to price the cart with Shopify while the shopper starts typing; prices from the
 * browser are never trusted.
 *
 * If the cart cannot be read, the shopper is sent to Shopify's own checkout, so a fault here
 * never blocks a sale.
 */
(() => {
  if (window.RiftCheckout) return;
  // Per store (layout/theme.liquid): which storefront this is, and whether the cart's own Checkout
  // buttons come here too (theriftshop.us) or only the VIP button does (riftshop.us).
  const settings = window.RiftCheckoutConfig || {};
  const STORE = String(settings.store || window.location.hostname.replace(/^www\./, ''));
  const INTERCEPT_CHECKOUT = settings.interceptCheckout !== false;
  const root = window.Shopify?.routes?.root || '/';
  const nativeCheckout = `${root}checkout`;
  const CHECKOUT_ORIGIN = 'https://securecheckout.riftshop.us';
  const CHECKOUT_URL = `${CHECKOUT_ORIGIN}/checkout`;
  let leaving = false;

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  };

  // Warm the connections the checkout page needs while the shopper is still browsing.
  for (const [rel, href] of [['preconnect', CHECKOUT_ORIGIN], ['preconnect', 'https://cdn.whop.com'], ['dns-prefetch', 'https://api.whop.com']]) {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    document.head.appendChild(link);
  }
  let prefetched = false;
  const prefetchCheckout = () => {
    if (prefetched) return;
    prefetched = true;
    for (const href of [CHECKOUT_URL, 'https://cdn.whop.com/elements/amber/elements.js']) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      document.head.appendChild(link);
    }
  };

  // Coming back with the Back button restores this page from memory: allow checking out again.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      leaving = false;
      document.documentElement.classList.remove('rift-checkout-loading');
    }
  });

  // First-touch campaign data, kept for this browser session so the handoff can carry it.
  try {
    const params = new URLSearchParams(window.location.search);
    if (!sessionStorage.getItem('riftAttribution')) {
      const touch = {};
      for (const [key, name] of [['utm_source', 'utmSource'], ['utm_medium', 'utmMedium'], ['utm_campaign', 'utmCampaign'], ['utm_term', 'utmTerm'], ['utm_content', 'utmContent']]) {
        if (params.get(key)) touch[name] = params.get(key);
      }
      if (params.get('fbclid')) touch.fbclid = params.get('fbclid');
      if (document.referrer && !document.referrer.startsWith(window.location.origin)) touch.referrer = document.referrer;
      if (Object.keys(touch).length) sessionStorage.setItem('riftAttribution', JSON.stringify(touch));
    }
  } catch (_) { /* storage blocked: attribution is optional */ }

  // Shopify's consent API is not loaded on every page. Start loading it as soon as this script
  // runs, so it is ready when the shopper taps instead of being waited on afterwards.
  let consentPromise = null;
  const loadConsent = () => {
    consentPromise ??= new Promise((resolve) => {
      if (window.Shopify?.customerPrivacy) return resolve(window.Shopify.customerPrivacy);
      if (!window.Shopify?.loadFeatures) return resolve(null);
      const timer = setTimeout(() => resolve(window.Shopify?.customerPrivacy || null), 1500);
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        clearTimeout(timer);
        resolve(error ? null : window.Shopify?.customerPrivacy || null);
      });
    });
    return consentPromise;
  };
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
  idle(() => { loadConsent(); });

  // Shopify keeps lines with the same variant but different properties (a bundle tag, say) as
  // separate cart lines. The checkout prices per variant and plan, so sum them into one line each.
  const linesFrom = (cart) => {
    const merged = new Map();
    for (const item of cart.items) {
      const plan = item.selling_plan_allocation?.selling_plan?.id;
      const key = `${item.variant_id}|${plan ?? ''}`;
      const line = merged.get(key) ?? { variantId: item.variant_id, quantity: 0, ...(plan ? { sellingPlanId: plan } : {}) };
      line.quantity += item.quantity;
      merged.set(key, line);
    }
    return Array.from(merged.values());
  };

  const base64url = (text) => btoa(unescape(encodeURIComponent(text))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  async function checkoutUrlFor(knownCart, added) {
    let cart = knownCart;
    if (!cart?.items) {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('cart unavailable');
      cart = await response.json();
    }
    if (!cart.items?.length) throw new Error('empty cart');

    const request = { store: STORE, lines: linesFrom(cart) };
    // Report the storefront's AddToCart from the checkout: Shopify's own Meta app would fire it a
    // moment after the add, but the shopper has already left for the checkout by then.
    if (added?.variantId) {
      request.added = { variantId: added.variantId, quantity: added.quantity || 1 };
      request.addedFrom = window.location.origin + window.location.pathname;
    }
    const privacy = await loadConsent();
    let marketingAllowed = false;
    try {
      marketingAllowed = typeof privacy?.marketingAllowed === 'function' ? privacy.marketingAllowed() === true : privacy?.marketingAllowed === true;
    } catch (_) { marketingAllowed = false; }
    request.marketingAllowed = marketingAllowed;
    if (marketingAllowed) {
      const fbp = readCookie('_fbp');
      let fbc = readCookie('_fbc');
      let touch = {};
      try { touch = JSON.parse(sessionStorage.getItem('riftAttribution') || '{}'); } catch (_) { touch = {}; }
      if (!fbc && touch.fbclid) fbc = `fb.1.${Date.now()}.${touch.fbclid}`;
      if (fbp) request.fbp = fbp;
      if (fbc) request.fbc = fbc;
      for (const key of ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent', 'referrer']) {
        if (touch[key]) request[key] = touch[key];
      }
    }
    return `${CHECKOUT_URL}#c=${base64url(JSON.stringify(request))}`;
  }

  /**
   * Go to the custom checkout. `cart` may be passed when the caller already holds the fresh cart;
   * `added` is the product the shopper just added, when this follows an Add to cart.
   */
  function goToCheckout(cart, added) {
    if (leaving) return Promise.resolve();
    leaving = true;
    document.documentElement.classList.add('rift-checkout-loading');
    return checkoutUrlFor(cart, added)
      .then((url) => { window.location.assign(url); })
      .catch((error) => {
        console.warn('[rift-checkout] falling back to Shopify checkout:', error?.message || error);
        window.location.assign(nativeCheckout);
      });
  }

  window.RiftCheckout = { goToCheckout };

  // Fetch the checkout page in the background as soon as the shopper shows intent.
  document.addEventListener('pointerdown', (event) => {
    const intent = INTERCEPT_CHECKOUT ? 'button[name="add"], button[name="checkout"], #CartDrawer-Checkout, [data-vip-atc]' : '[data-vip-atc]';
    if (event.target.closest?.(intent)) prefetchCheckout();
  }, { passive: true, capture: true });

  // riftshop.us: only the VIP button comes to this checkout; the cart's Checkout buttons stay Shopify's.
  if (!INTERCEPT_CHECKOUT) return;

  // Cart page and cart drawer: their Checkout buttons submit the cart form with name="checkout".
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href$="/checkout"], #checkout, #CartDrawer-Checkout');
    if (!button || button.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    goToCheckout();
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.submitter?.name === 'checkout' || event.target?.action?.endsWith?.('/checkout')) {
      event.preventDefault();
      goToCheckout();
    }
  }, true);
})();
